import { useState, useEffect } from 'react';

/**
 * Hook pour charger dynamiquement les données d'un projet
 * @param {string} slug - Slug du projet (ex: 'guyane-2026')
 * @returns {object} { project, loading, error }
 */
export function useProject(slug) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadProject() {
      if (!slug) {
        setError('No project slug provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Import dynamique des fichiers du projet
        const [configModule, contentModule, formulesModule, planningModule] = await Promise.all([
          import(`../projects/${slug}/config.js`),
          import(`../projects/${slug}/content.js`),
          import(`../projects/${slug}/formules.js`),
          import(`../projects/${slug}/planning.js`),
        ]);

        // Agrégation des données
        const projectData = {
          ...configModule.projectConfig,
          content: contentModule.content,
          formules: formulesModule.formules,
          comparison: formulesModule.comparison,
          savings: formulesModule.savings,
          planning: planningModule.planning,
        };

        setProject(projectData);
      } catch (err) {
        console.error('Error loading project:', err);
        setError(`Project "${slug}" not found or invalid`);
      } finally {
        setLoading(false);
      }
    }

    loadProject();
  }, [slug]);

  return { project, loading, error };
}

/**
 * Hook pour lister tous les projets disponibles
 * @returns {object} { projects, loading, error }
 */
export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadProjects() {
      try {
        setLoading(true);

        // Import du fichier index des projets
        const indexModule = await import('../projects/index.js');
        const projectsList = indexModule.projects || [];

        // Charger les configs de chaque projet
        const loadedProjects = await Promise.all(
          projectsList.map(async (slug) => {
            try {
              const configModule = await import(`../projects/${slug}/config.js`);
              return configModule.projectConfig;
            } catch (err) {
              console.warn(`Could not load project: ${slug}`, err);
              return null;
            }
          })
        );

        setProjects(loadedProjects.filter(Boolean));
      } catch (err) {
        console.error('Error loading projects:', err);
        setError('Could not load projects list');
      } finally {
        setLoading(false);
      }
    }

    loadProjects();
  }, []);

  return { projects, loading, error };
}
